import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid2';
import { useContext, useState, useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import TripRateCalculationViewModel from '../ViewModels/TripRateCalculationViewModel';
import TripRateDay from '../ViewModels/TripRateDay';
import CrossTestViewModel from '../ViewModels/CrossTestViewModel';

import { APIContext } from '../../Contexts/APIContext';
import { SessionContext } from '../../Contexts/SessionContext';

interface IProps {
    dialogOpen: boolean;
    handleClose: () => void;
    tripRate: TripRateCalculationViewModel;
    hourly: boolean;
    surveyRefs: string[];
    vehicleType: number;
}

export default function CrossTestDialog({
    dialogOpen,
    handleClose,
    tripRate,
    vehicleType,
    hourly,
    surveyRefs,
}: IProps) {
    const [direction, setDirection] = useState('Totals');
    const [startTime, setStartTime] = useState('12:00');
    const [endTime, setEndTime] = useState('13:00');
    const [crossTest, setCrossTest] = useState(new CrossTestViewModel());

    const { sessionState } = useContext(SessionContext);
    const { post, loading } = useContext(APIContext);

    const formatDisplayTime = (time: string) => (time === '00:00' ? '24:00' : time);

    const getFilteredPeriods = () => {
        const list = tripRate[`${direction.toLowerCase()}` as keyof TripRateCalculationViewModel] ?? [];
        const reducedList = (list as TripRateDay[]).filter(
            (time) => time.numberOfDays != null && time.numberOfDays > 0
        );

        const periodsSet = new Set<string>();
        reducedList.forEach((time) => {
            const [start, end] = time.period?.split('-') ?? [];
            if (start) periodsSet.add(start);
            if (end) periodsSet.add(end);
        });

        return Array.from(periodsSet).map((period) => (
            <MenuItem key={period} value={period}>
                {formatDisplayTime(period)}
            </MenuItem>
        ));
    };

    const timeToIndex = (time: string, hourlyMode: boolean): number => {
        const [hourStr, minuteStr] = time.split(':');
        let hour = parseInt(hourStr, 10);
        const minute = parseInt(minuteStr, 10);

        if (hour === 0) {
            hour = 24;
        }

        let index = hour;

        if (!hourlyMode) {
            index = index * 2;
            if (minute >= 30) index += 1;
        }
        return index;
    };

    const getData = () => {
        const startTimeIndex = timeToIndex(startTime, hourly);
        const endTimeIndex = timeToIndex(endTime, hourly);

        post(
            `TripRateCalculation`,
            `GetCrossTestValues?headerIndex=${sessionState.primaryFilters.headerIndex}` +
            `&landUse=${sessionState.tripRateMain}` +
            `&subUse=${sessionState.tripRateSub}` +
            `&startTimeIndex=${startTimeIndex}` +
            `&endTimeIndex=${endTimeIndex}` +
            `&vehicleType=${vehicleType}` +
            `&hourly=${hourly}`,
            surveyRefs,
            setCrossTest
        );
    };

    const onChangeStart = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStartTime(e.target.value);
    };

    const onChangeEnd = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEndTime(e.target.value);
    };

    useEffect(() => {
        getData();
    }, [direction, startTime, endTime]);

    const dirLower = direction.toLowerCase();

    return (
        <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>CROSS TEST</DialogTitle>
            <DialogContent>
                <Grid container size={12} spacing={2}>
                    <Grid size={3}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Direction
                        </Typography>
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={direction}
                                onChange={(e) => setDirection((e.target as HTMLInputElement).value)}
                            >
                                <FormControlLabel value="Arrivals" control={<Radio />} label="Arrivals" />
                                <FormControlLabel value="Departures" control={<Radio />} label="Departures" />
                                <FormControlLabel value="Totals" control={<Radio />} label="Totals" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    <Grid size={4}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Start/End Time Selection
                        </Typography>

                        <TextField
                            select
                            label="Start"
                            value={startTime}
                            onChange={onChangeStart}
                            fullWidth
                            margin="dense"
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        sx: { maxHeight: 300 },
                                    },
                                },
                            }}
                            sx={{ mb: 1 }}
                        >
                            {getFilteredPeriods()}
                        </TextField>

                        <TextField
                            select
                            label="End"
                            value={endTime}
                            onChange={onChangeEnd}
                            fullWidth
                            margin="dense"
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        sx: { maxHeight: 300 },
                                    },
                                },
                            }}
                        >
                            {getFilteredPeriods()}
                        </TextField>
                    </Grid>

                    <Grid size={5}>
                        <Box
                            sx={{
                                minHeight: 220,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 1, 
                            }}
                        >
                            {loading ? (
                                <CircularProgress />
                            ) : (
                                <Stack spacing={2} sx={{ width: 1 }}>
                                    <Typography>
                                        Selected Time Period: {startTime} - {endTime}
                                    </Typography>
                                    <Typography>Direction: {direction}</Typography>

                                    {dirLower === 'arrivals' && (
                                        <>
                                            <Typography>Mean Trip Rate: {crossTest.arrivalMean}</Typography>
                                            <Typography>Median Trip Rate: {crossTest.arrivalMedian}</Typography>
                                            <Typography>Variation (%): {crossTest.arrivalVariation}%</Typography>
                                        </>
                                    )}

                                    {dirLower === 'departures' && (
                                        <>
                                            <Typography>Mean Trip Rate: {crossTest.departureMean}</Typography>
                                            <Typography>Median Trip Rate: {crossTest.departureMedian}</Typography>
                                            <Typography>Variation (%): {crossTest.departureVariation}%</Typography>
                                        </>
                                    )}

                                    {dirLower === 'totals' && (
                                        <>
                                            <Typography>Mean Trip Rate: {crossTest.totalMean}</Typography>
                                            <Typography>Median Trip Rate: {crossTest.totalMedian}</Typography>
                                            <Typography>Variation (%): {crossTest.totalVariation}%</Typography>
                                        </>
                                    )}
                                </Stack>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
